import { useState, useEffect } from 'react';
import { CalendarDays, MapPin, Clock, Ticket, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Events = () => {
  const [events, setEvents] = useState([]);
  const [pastEvents, setPastEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPast, setShowPast] = useState(false);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const [upcomingRes, allRes] = await Promise.all([
        axios.get(`${API_URL}/events?upcoming=true`),
        axios.get(`${API_URL}/events/all`)
      ]);
      setEvents(upcomingRes.data);
      
      // Filter past events
      const today = new Date().toISOString().split('T')[0];
      const past = allRes.data.filter(e => e.date < today);
      setPastEvents(past);
    } catch (error) {
      console.error('Failed to fetch events:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen py-20 px-6" data-testid="events-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-12 text-center">
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
            <span className="gradient-text">Events</span>
          </h1>
          <p className="text-zinc-400 text-lg">
            Catch DJ Shadow live at these venues
          </p>
        </div>

        {/* Upcoming Events */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
            <CalendarDays className="w-6 h-6 text-[#D9F99D]" />
            Upcoming Events
          </h2>

          {loading ? (
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} className="h-40 rounded-2xl bg-zinc-800/50" />
              ))}
            </div>
          ) : events.length > 0 ? (
            <div className="space-y-4">
              {events.map((event) => (
                <EventCard key={event.id} event={event} />
              ))}
            </div>
          ) : (
            <div className="glass-card rounded-2xl p-12 text-center">
              <CalendarDays className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-2">No upcoming events</h3>
              <p className="text-zinc-400">Stay tuned for announcements!</p>
            </div>
          )}
        </section>

        {/* Past Events Toggle */}
        {pastEvents.length > 0 && (
          <section>
            <button
              onClick={() => setShowPast(!showPast)}
              className="w-full text-left mb-6"
            >
              <h2 className="text-2xl font-bold flex items-center gap-2 text-zinc-400 hover:text-white transition-colors">
                Past Events ({pastEvents.length})
                <span className={`transition-transform ${showPast ? 'rotate-180' : ''}`}>▼</span>
              </h2>
            </button>

            {showPast && (
              <div className="space-y-4 opacity-70">
                {pastEvents.map((event) => (
                  <EventCard key={event.id} event={event} isPast />
                ))}
              </div>
            )}
          </section>
        )}
      </div>
    </div>
  );
};

const EventCard = ({ event, isPast = false }) => {
  // Parse date without timezone issues
  const [year, month, day] = event.date.split('-').map(Number);
  const eventDate = new Date(year, month - 1, day);
  const monthStr = eventDate.toLocaleDateString('en-US', { month: 'short' }).toUpperCase();
  const weekday = eventDate.toLocaleDateString('en-US', { weekday: 'long' });

  return (
    <div 
      className={`glass-card rounded-2xl overflow-hidden transition-all duration-300 ${
        isPast ? '' : 'hover:border-[#D9F99D]/30'
      }`}
      data-testid={`event-${event.id}`}
    >
      {event.cover_url && (
        <div className="w-full">
          <img 
            src={event.cover_url} 
            alt={event.title}
            className="w-full h-auto object-contain"
          />
        </div>
      )}
      
      <div className="p-6">
        <div className="flex gap-6">
          {/* Date */}
          <div className="w-20 flex-shrink-0">
            <div className={`rounded-xl p-3 text-center ${
              isPast ? 'bg-zinc-800' : 'bg-[#D9F99D]/10'
            }`}>
              <span className={`text-xs font-bold block ${
                isPast ? 'text-zinc-500' : 'text-[#D9F99D]'
              }`}>{monthStr}</span>
              <span className="text-3xl font-bold block">{day}</span>
              <span className="text-xs text-zinc-500">{weekday}</span>
            </div>
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <h3 className="text-xl font-bold mb-2">{event.title}</h3>
            
            <div className="space-y-2 text-sm text-zinc-400">
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                <span>{event.venue}</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                <span>{event.location}</span>
              </div>
              {event.time && (
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  <span>{event.time}</span>
                </div>
              )}
            </div>

            {event.description && (
              <p className="text-zinc-500 text-sm mt-3">{event.description}</p>
            )}

            {event.ticket_url && !isPast && (
              <a 
                href={event.ticket_url}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center gap-2"
              >
                <Button className="bg-[#D9F99D] text-black hover:bg-[#BEF264] rounded-full">
                  <Ticket className="w-4 h-4 mr-2" />
                  Get Tickets
                  <ExternalLink className="w-3 h-3 ml-1" />
                </Button>
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Events;
